// src/components/extensions/FoldDemo.styles.ts
// Only what is specific to folding: the gutter control, the collapsible region,
// and the summary that stands in for it.
//
// The editor frame, file tab, code area and gutter are shared with the other
// demos and live in editor.styles.ts.
import styled from 'styled-components';
import { motion } from 'framer-motion';

import { CodeText, monoStack, noMotion } from './editor.styles';

/**
 * The two lines that bound the foldable region. Accent, because they are the
 * region's handles: a glance should show what is foldable, and after the fold
 * these are the only lines left of it.
 */
export const BoundaryText = styled(CodeText)`
  color: ${({ theme }) => theme.accent};
`;

/**
 * The foldable body. Height is animated to 0, so it must clip; `overflow: hidden`
 * is doing real work here rather than tidying up a shadow.
 */
export const FoldableBody = styled(motion.div)`
  overflow: hidden;
`;

/* ----------------------------------------------------------- fold gutter */

/**
 * The fold control, in the gutter where VS Code puts it.
 *
 * A real button: the demo is interactive, so it has to be reachable by keyboard
 * and announce its state. `aria-expanded` is what tells a screen reader whether
 * the region is open, since the chevron alone is a shape.
 */
export const FoldToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.1rem;
  height: 1.1rem;
  padding: 0;
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease;

  /* Neutralise the global button reset just enough to be a button, without
     inheriting a look meant for the site's own buttons. */
  font: inherit;

  &:hover {
    color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.cardBg};
  }

  &&:focus-visible {
    outline: none;
    color: ${({ theme }) => theme.accent};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.surface},
      0 0 0 3px ${({ theme }) => theme.accent};
  }

  ${noMotion}
`;

/**
 * The chevron, rotated rather than swapped between two glyphs. A single element
 * that turns into its own "closed" state animates; a crossfade between two icons
 * would flicker, and the rotation matches how the real editor moves.
 */
export const Chevron = styled(motion.span)`
  display: inline-flex;
  line-height: 0;
  transform-origin: 50% 50%;
`;

/* -------------------------------------------------------------- summary */

/**
 * Stands in for the folded lines, the way VS Code's own fold placeholder does.
 * Accent, because it is a control, not a fragment of code: it is where the
 * reader clicks to get the code back.
 */
export const FoldSummary = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 1.275rem;
  padding-left: 3rem;
  font-family: ${monoStack};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.accent};
  cursor: pointer;

  svg {
    flex-shrink: 0;
  }

  ${noMotion}
`;

/** Just the collapsed line count, e.g. "7 lines folded". */
export const FoldSummaryText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* -------------------------------------------------------------- keycaps */

/**
 * The shortcut the extension binds folding to, shown as real keycaps.
 *
 * Present because it is one of the extension's listed features and a fold that
 * happens by itself does not explain how the user would trigger it. The caps are
 * never interactive: they are documentation, so they read as pressed only while
 * the demo is the thing doing the folding.
 */
export const ShortcutRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
`;

export const ShortcutLabel = styled.span`
  margin-right: auto;
  font-family: ${monoStack};
  font-size: 0.65rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
`;

/**
 * A keycap. Inset border and a surface fill, so a cap looks pressed when the
 * transform pushes it down rather than changing colour — the depth cue is what
 * sells a key press.
 */
export const KeyCap = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  padding: 0.15rem 0.35rem;
  background: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-bottom-width: 2px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-family: ${monoStack};
  font-size: 0.65rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.text};
`;
